import {
  ResponseError,
  type PopupProps,
  SurvivalMode as SurvivalModePopup
} from '@CS/components/Popups';

import API from '@/services/api';
import { MCD } from '@/services/app';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import type { ResponseErrorInfo } from '@/services/api/types';
import { setUserPoints, getUserPoints, getUserEntityData } from '@/redux/user';

type SurvivalModeProps = PopupProps & {
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  maxUse?: number;
  score?: number;
  price: number;
};

export const SurvivalMode = ({
  maxUse = Infinity,
  onConfirm,
  price,
  ...props
}: SurvivalModeProps) => {
  const dispatch = useAppDispatch();

  const points = useAppSelector(getUserPoints);
  const userData = useAppSelector(getUserEntityData);

  const { onCancel = () => void 0, onClose } = props;
  const cancelSurvivalMode = () => setSurvivalModeError(undefined);

  let survivalModes = ((userData as Record<string, unknown>)?.survivalModes ??
    0) as number;

  const [survivalModeError, setSurvivalModeError] =
    useState<ResponseErrorInfo>();

  const onSubmit = () => {
    onConfirm?.();
    survivalModes++;

    Promise.all([
      MCD.burnUserPoints(price),
      API.updateUser({ data: { survivalModes } })
    ])
      .then(() => dispatch(setUserPoints(points - price)))
      .catch(async (error: Response) => {
        setSurvivalModeError(await API.getResponseErrorInfo(error));
        throw error;
      });
  };

  useEffect(() => {
    if (survivalModes === maxUse) {
      onCancel();
      onClose();
    }
  }, [survivalModes, maxUse, onCancel, onClose]);

  return (
    <>
      <SurvivalModePopup
        onSubmit={onSubmit}
        onCancel={onCancel}
        points={points}
        price={price}
        {...props}
      />

      {survivalModeError && (
        <ResponseError info={survivalModeError} onClose={cancelSurvivalMode} />
      )}
    </>
  );
};
